/**
 * Pure helpers for the Kaption "New message" (new conversation) modal.
 *
 * Everything here is framework-free and side-effect-free so it can be unit
 * tested in isolation (see spec/newConversationHelpers.spec.js). The Vue
 * components import these helpers for recipient parsing, template gallery
 * filtering, variable extraction, smart defaults, validation and payload
 * building.
 */
import { parsePhoneNumberFromString } from 'libphonenumber-js';

export const TEMPLATE_CATEGORIES = ['UTILITY', 'MARKETING', 'AUTHENTICATION'];
const MEDIA_HEADER_FORMATS = ['IMAGE', 'VIDEO', 'DOCUMENT'];
const DATE_TOKEN_RE =
  /(date|day|fecha|data|datum|jour|when|schedul|appt|appointment)/i;
const NAME_TOKEN_RE = /(name|nombre|nome|first[_\s-]?name)/i;

/**
 * Detect whether the user is typing a raw phone number rather than a name.
 * We accept a leading "+" but do NOT require it. A value counts as a number
 * when it is made only of phone characters and contains at least 6 digits.
 */
export const looksLikePhoneNumber = input => {
  if (!input) return false;
  const value = String(input).trim();
  if (!/^[+]?[\d\s().-]+$/.test(value)) return false;
  const digits = value.replace(/\D/g, '');
  return digits.length >= 6;
};

/**
 * Derive the default country (ISO-2) to use when the user types a local
 * number without a country code. We read it from the WhatsApp inbox's own
 * phone number. Falls back to `fallback` (default 'US').
 */
export const deriveDefaultCountry = (inbox, fallback = 'US') => {
  const raw =
    inbox?.phoneNumber || inbox?.phone_number || inbox?.messagingServiceSid;
  if (raw) {
    const parsed = parsePhoneNumberFromString(String(raw));
    if (parsed?.country) return parsed.country;
  }
  return fallback;
};

/**
 * Normalise a raw user-typed recipient into an E.164 number.
 * - A leading "+" is honoured and parsed internationally.
 * - "00" international prefix is supported.
 * - Otherwise the number is parsed against `defaultCountry` (from the inbox).
 *
 * Returns `{ e164, isValid, national }`. `isValid` gates create-on-send.
 */
export const normalizeToE164 = (input, defaultCountry = 'US') => {
  const value = String(input || '').trim();
  if (!value) return { e164: '', isValid: false, national: '' };

  let parsed;
  if (value.startsWith('+')) {
    parsed = parsePhoneNumberFromString(value);
  } else if (value.startsWith('00')) {
    parsed = parsePhoneNumberFromString(`+${value.slice(2)}`);
  } else {
    parsed = parsePhoneNumberFromString(value, defaultCountry);
  }

  if (parsed && parsed.isValid()) {
    return {
      e164: parsed.number,
      isValid: true,
      national: parsed.formatNational(),
    };
  }

  // Best-effort fallback so the preview/label still shows something while the
  // user keeps typing; isValid stays false so Send remains blocked.
  const digits = value.replace(/\D/g, '');
  const e164 = value.startsWith('+') ? `+${digits}` : `+${digits}`;
  return { e164, isValid: false, national: value };
};

/* ------------------------------------------------------------------ *
 * Template gallery                                                     *
 * ------------------------------------------------------------------ */

const bodyText = template => {
  const body = template?.components?.find(c => c.type === 'BODY');
  return body?.text || '';
};

/** True for internal Kaption-reserved templates we hide from the gallery. */
export const isInternalTemplate = template =>
  Boolean(template?.name && /^kaption_/i.test(template.name));

/**
 * Filter the approved template list for the gallery.
 * @param {Array}  templates raw approved template objects
 * @param {Object} opts { query, category, language, hideInternal }
 */
export const filterTemplates = (templates = [], opts = {}) => {
  const {
    query = '',
    category = 'ALL',
    language = 'ALL',
    hideInternal = true,
  } = opts;
  const needle = String(query).trim().toLowerCase();

  return templates.filter(template => {
    if (!template) return false;
    if (hideInternal && isInternalTemplate(template)) return false;

    if (category !== 'ALL') {
      const tplCategory = (template.category || 'UTILITY').toUpperCase();
      if (tplCategory !== String(category).toUpperCase()) return false;
    }

    if (language !== 'ALL') {
      if ((template.language || '') !== language) return false;
    }

    if (needle) {
      const haystack =
        `${template.name || ''} ${bodyText(template)}`.toLowerCase();
      if (!haystack.includes(needle)) return false;
    }

    return true;
  });
};

/** Unique category tabs present in the given templates (ordered canonically). */
export const getAvailableCategories = (templates = []) => {
  const present = new Set(
    templates
      .filter(t => t && !isInternalTemplate(t))
      .map(t => (t.category || 'UTILITY').toUpperCase())
  );
  return TEMPLATE_CATEGORIES.filter(c => present.has(c));
};

/** Unique language codes present in the given templates. */
export const getAvailableLanguages = (templates = []) => {
  const present = new Set(
    templates
      .filter(t => t && !isInternalTemplate(t))
      .map(t => t.language)
      .filter(Boolean)
  );
  return Array.from(present).sort();
};

/* ------------------------------------------------------------------ *
 * Variables + header                                                  *
 * ------------------------------------------------------------------ */

/** The HEADER component (if any). */
export const getHeaderComponent = template =>
  template?.components?.find(c => c.type === 'HEADER') || null;

/** True when the template has an IMAGE/VIDEO/DOCUMENT header needing an upload. */
export const hasMediaHeader = template => {
  const header = getHeaderComponent(template);
  return Boolean(header && MEDIA_HEADER_FORMATS.includes(header.format));
};

/** Lowercased media type ('image'|'video'|'document') or null. */
export const mediaHeaderType = template => {
  const header = getHeaderComponent(template);
  if (!header || !MEDIA_HEADER_FORMATS.includes(header.format)) return null;
  return header.format.toLowerCase();
};

/** TEXT header string (for non-media headers), or ''. */
export const getHeaderText = template => {
  const header = getHeaderComponent(template);
  return header && header.format === 'TEXT' ? header.text || '' : '';
};

/** FOOTER text, or ''. */
export const getTemplateFooter = template => {
  const footer = template?.components?.find(c => c.type === 'FOOTER');
  return footer?.text || '';
};

/**
 * Parse a template's BUTTONS into a render/validation-friendly list.
 * Each entry: { index, type, text, url, phone_number, needsParam, paramKind }
 *  - QUICK_REPLY / PHONE_NUMBER / static URL -> needsParam:false
 *  - URL containing {{ }}                      -> needsParam:true,  paramKind:'url'
 *  - COPY_CODE                                 -> needsParam:true,  paramKind:'copy_code'
 */
export const parseTemplateButtons = template => {
  const comp = template?.components?.find(c => c.type === 'BUTTONS');
  if (!comp || !Array.isArray(comp.buttons)) return [];
  return comp.buttons.map((btn, index) => {
    const type = btn.type || 'QUICK_REPLY';
    const isDynamicUrl = type === 'URL' && !!btn.url && btn.url.includes('{{');
    const isCopyCode = type === 'COPY_CODE';
    let paramKind = null;
    if (isDynamicUrl) paramKind = 'url';
    else if (isCopyCode) paramKind = 'copy_code';
    return {
      index,
      type,
      text: btn.text || btn.title || btn.label || '',
      url: btn.url || '',
      phone_number: btn.phone_number || '',
      needsParam: isDynamicUrl || isCopyCode,
      paramKind,
      variables: isDynamicUrl
        ? (btn.url.match(/{{\s*([^}]+?)\s*}}/g) || []).map(v =>
            v.replace(/{{\s*|\s*}}/g, '').trim()
          )
        : [],
    };
  });
};

/** Buttons in this template that require a user-supplied parameter. */
export const getButtonsNeedingParam = template =>
  parseTemplateButtons(template).filter(b => b.needsParam);

/**
 * Extract ordered, de-duplicated body variables from a template.
 * Returns `[{ token: '{{1}}', key: '1' }, ...]`.
 */
export const parseBodyVariables = template => {
  const text = bodyText(template);
  const matches = text.match(/{{\s*([^}]+?)\s*}}/g) || [];
  const seen = new Set();
  const vars = [];
  matches.forEach(token => {
    const key = token.replace(/{{\s*|\s*}}/g, '').trim();
    if (!seen.has(key)) {
      seen.add(key);
      vars.push({ token, key });
    }
  });
  return vars;
};

const todayLabel = (now = new Date()) =>
  now.toLocaleDateString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });

/**
 * Smart default for a single variable, mirroring the campaign builder:
 *  - positional {{1}} or a name-ish token  -> contact name
 *  - a date-ish token                       -> today's date
 *  - otherwise                              -> ''
 */
export const computeSmartDefault = (key, { contact = null, now } = {}) => {
  const name =
    contact?.name || contact?.phoneNumber || contact?.phone_number || '';
  const normalized = String(key).trim();

  if (normalized === '1' && name) return name;
  if (NAME_TOKEN_RE.test(normalized) && name) return name;
  if (DATE_TOKEN_RE.test(normalized)) return todayLabel(now);
  return '';
};

/** Build the initial body values map with smart defaults applied. */
export const buildInitialBodyValues = (template, context = {}) => {
  const values = {};
  parseBodyVariables(template).forEach(({ key }) => {
    values[key] = computeSmartDefault(key, context);
  });
  return values;
};

/* ------------------------------------------------------------------ *
 * Validation                                                          *
 * ------------------------------------------------------------------ */

/**
 * True when every required variable, media header, and dynamic button
 * parameter has been provided. Gates the Send button.
 */
export const isComposeValid = (
  template,
  bodyValues = {},
  headerMedia = {},
  buttonValues = {}
) => {
  if (!template) return false;

  const filled = value =>
    value !== undefined && value !== null && String(value).trim() !== '';

  const vars = parseBodyVariables(template);
  if (!vars.every(({ key }) => filled(bodyValues[key]))) return false;

  if (hasMediaHeader(template)) {
    if (!filled(headerMedia?.media_url)) return false;
  }

  const dynamicButtons = getButtonsNeedingParam(template);
  if (!dynamicButtons.every(b => filled(buttonValues[b.index]))) return false;

  return true;
};

/* ------------------------------------------------------------------ *
 * Rendering + payload                                                 *
 * ------------------------------------------------------------------ */

/** Interpolate the body text with the filled values for the message content. */
export const renderTemplateBody = (template, bodyValues = {}) => {
  const text = bodyText(template);
  return text.replace(/{{\s*([^}]+?)\s*}}/g, (match, token) => {
    const key = token.trim();
    const value = bodyValues[key];
    return value !== undefined && value !== null && String(value) !== ''
      ? String(value)
      : match;
  });
};

/**
 * Assemble the `processed_params` object (body/header/buttons) sent to the
 * backend as part of `template_params`.
 */
export const buildProcessedParams = (
  template,
  bodyValues = {},
  headerMedia = null,
  buttonValues = {}
) => {
  const processed = {};

  const vars = parseBodyVariables(template);
  if (vars.length) {
    processed.body = {};
    vars.forEach(({ key }) => {
      processed.body[key] = bodyValues[key] ?? '';
    });
  }

  if (hasMediaHeader(template)) {
    processed.header = {
      media_url: headerMedia?.media_url || '',
      media_type: mediaHeaderType(template),
    };
    if (mediaHeaderType(template) === 'document') {
      processed.header.media_name = headerMedia?.media_name || '';
    }
  }

  // Only dynamic buttons (URL-with-vars, COPY_CODE) carry params; the array is
  // sparse and indexed by the button's position, mirroring buildTemplateParameters.
  const dynamicButtons = getButtonsNeedingParam(template);
  if (dynamicButtons.length) {
    const buttons = [];
    dynamicButtons.forEach(b => {
      if (b.paramKind === 'url') {
        buttons[b.index] = {
          type: 'url',
          parameter: buttonValues[b.index] ?? '',
          url: b.url,
          variables: b.variables,
        };
      } else if (b.paramKind === 'copy_code') {
        buttons[b.index] = {
          type: 'copy_code',
          parameter: buttonValues[b.index] ?? '',
        };
      }
    });
    processed.buttons = buttons;
  }

  return processed;
};

/** Full `template_params` payload for the create-conversation message. */
export const buildTemplateParams = (
  template,
  bodyValues = {},
  headerMedia = null,
  buttonValues = {}
) => ({
  name: template?.name,
  category: template?.category,
  language: template?.language,
  namespace: template?.namespace,
  processed_params: buildProcessedParams(
    template,
    bodyValues,
    headerMedia,
    buttonValues
  ),
});

/**
 * Body-variable map keyed by index for the Kaption Scheduled Messages API
 * (`variables: { "1": "value" }`). The scheduled-message job resolves the
 * template body from this map at delivery time.
 */
export const buildScheduleVariables = (template, bodyValues = {}) => {
  const map = {};
  parseBodyVariables(template).forEach(({ key }) => {
    map[key] = bodyValues[key] ?? '';
  });
  return map;
};

/** Payload for POST .../kaption_scheduled_messages (a template scheduled send). */
export const buildScheduledMessagePayload = ({
  template,
  bodyValues = {},
  scheduledAt,
  timezone,
}) => ({
  scheduled_at: scheduledAt,
  timezone,
  message_type: 'template',
  template_name: template?.name,
  variables: buildScheduleVariables(template, bodyValues),
});

/**
 * Build the exact payload dispatched to `contactConversations/create` for a
 * WhatsApp template message. Mirrors `prepareWhatsAppMessagePayload` so the
 * create-on-send path reuses the existing conversations endpoint contract.
 */
export const buildWhatsAppConversationPayload = ({
  inbox,
  contact,
  sourceId,
  content,
  templateParams,
  currentUser,
}) => ({
  inboxId: inbox?.id,
  sourceId: sourceId ?? inbox?.sourceId,
  contactId: contact?.id,
  message: { content, template_params: templateParams },
  assigneeId: currentUser?.id,
});
